import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import {
  Input,
  MultiInput,
  InputChips,
  CurrencyInput,
  Button,
  IconInfo,
  THEMES,
  Switch,
  ICON_SIZE,
  Checkbox,
  StatusIndicator,
  IconButton,
} from "@cdkglobal/radial";
import { ComponentProps } from "react";
import { uniqueId } from "underscore";
import { FEATURE_FLAG_KEYS } from "../LandingPage/constants";
import {
  InfoIconContainer,
  StyledTooltip,
} from "../../utils/styled/styled.recordPage";
import {
  RenderDropdownInputProps,
  RenderDatePickerProps,
  RenderMegaDateTimePickerProps,
} from "../../types";
import RenderDropdownInput from "../LandingPage/components/renderers/RenderDropdownInput";
import RenderDatePicker from "../LandingPage/components/renderers/RenderDatePicker";
import { RenderMegaDateTimePicker } from "../LandingPage/components/renderers/RenderMegaDateTimePicker";

// Tooltip wrapper component

const useTooltipId = () => `tooltip-id-${uniqueId()}`;

const FieldTooltip = ({ id, text }: { id: string; text: string }) => {
  const isTooltipEnabled: boolean = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_TOOLTIP,
    false,
  );

  if (!isTooltipEnabled || !text) return null;

  return <StyledTooltip id={id} text={text} theme={THEMES.LIGHT} />;
};

// ─── SwitchWithTooltip ────────────────────────────────────────────────────────

type SwitchWithTooltipProps = ComponentProps<typeof Switch> & {
  tooltip?: string;
};

export const SwitchWithTooltip = ({
  tooltip,
  ...props
}: SwitchWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <div data-tooltip-id={tooltip ? tooltipId : undefined}>
      <Switch {...props} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </div>
  );
};

// ─── CheckBoxWithTooltip ────────────────────────────────────────────────────────

type CheckboxWithTooltipProps = ComponentProps<typeof Checkbox> & {
  tooltip?: string;
};

export const CheckboxWithTooltip = ({
  tooltip,
  ...props
}: CheckboxWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <div data-tooltip-id={tooltip ? tooltipId : undefined}>
      <Checkbox {...props} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </div>
  );
};

// ─── InputWithTooltip ─────────────────────────────────────────────────────────

type InputWithTooltipProps = ComponentProps<typeof Input> & {
  tooltip?: string;
};

export const InputWithTooltip = ({
  tooltip,
  ...props
}: InputWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <Input {...props} data-tooltip-id={tooltip ? tooltipId : undefined} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

type CurrencyInputWithTooltipProps = ComponentProps<typeof CurrencyInput> & {
  tooltip?: string;
};

export const CurrencyInputWithTooltip = ({
  tooltip,
  ...props
}: CurrencyInputWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <CurrencyInput
        {...props}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── MultiInputWithTooltip ────────────────────────────────────────────────────

type MultiInputWithTooltipProps = ComponentProps<typeof MultiInput> & {
  tooltip?: string;
};

export const MultiInputWithTooltip = ({
  tooltip,
  ...props
}: MultiInputWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <MultiInput
        {...props}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── MultiInputChipsWithTooltip ───────────────────────────────────────────────

type MultiInputChipsWithTooltipProps = ComponentProps<typeof InputChips> & {
  tooltip?: string;
};

export const MultiInputChipsWithTooltip = ({
  tooltip,
  ...props
}: MultiInputChipsWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <InputChips
        {...props}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── ButtonWithTooltip ────────────────────────────────────────────────────────

type ButtonWithTooltipProps = ComponentProps<typeof Button> & {
  tooltip?: string;
};

export const ButtonWithTooltip = ({
  tooltip,
  ...props
}: ButtonWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <Button {...props} data-tooltip-id={tooltip ? tooltipId : undefined} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── IconButtonWithTooltip ───────────────────────────────────────────────

type IconButtonWithTooltipProps = ComponentProps<typeof IconButton> & {
  tooltip?: string;
};

export const IconButtonWithTooltip = ({
  tooltip,
  ...props
}: IconButtonWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <IconButton
        {...props}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── SectionInfoIconWithTooltip ───────────────────────────────────────────────

export const SectionInfoIconWithTooltip = ({
  tooltip,
  customStyle,
}: {
  tooltip: string;
  customStyle?: string;
}) => {
  const tooltipId = useTooltipId();
  const isTooltipEnabled: boolean = useFeatureFlag(
    FEATURE_FLAG_KEYS.ENABLE_TOOLTIP,
    false,
  );

  return (
    <InfoIconContainer $customStyle={customStyle}>
      <IconInfo
        data-tooltip-id={isTooltipEnabled ? tooltipId : undefined}
        aria-label={tooltip}
        tabIndex={0}
        size={ICON_SIZE.SMALL}
      />
      {isTooltipEnabled && tooltip && (
        <StyledTooltip id={tooltipId} text={tooltip} theme={THEMES.LIGHT} />
      )}
    </InfoIconContainer>
  );
};

type StatusIndicatorProps = ComponentProps<typeof StatusIndicator> & {
  tooltip?: string;
};

export const StatusIndicatorWithTooltip = ({
  tooltip,
  ...props
}: StatusIndicatorProps) => {
  const tooltipId = useTooltipId();
  return (
    <>
      <StatusIndicator
        {...props}
        data-tooltip-id={tooltip ? tooltipId : undefined}
      />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </>
  );
};

// ─── DropdownWithTooltip ───────────────────────────────────────────────

type DropdownWithTooltipProps = RenderDropdownInputProps & {
  tooltip?: string;
};

export const DropdownWithTooltip = ({
  tooltip,
  ...props
}: DropdownWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <div data-tooltip-id={tooltip ? tooltipId : undefined}>
      <RenderDropdownInput {...props} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </div>
  );
};

// ─── DatePickerWithTooltip ───────────────────────────────────────────────────

type DatePickerWithTooltipProps = RenderDatePickerProps & {
  tooltip?: string;
};

export const DatePickerWithTooltip = ({
  tooltip,
  ...props
}: DatePickerWithTooltipProps) => {
  const tooltipId = useTooltipId();
  return (
    <div data-tooltip-id={tooltip ? tooltipId : undefined}>
      <RenderDatePicker {...props} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </div>
  );
};

// ─── MegaDateTimePickerWithTooltip ───────────────────────────────────────────────

type MegaDateTimePickerWithTooltip = RenderMegaDateTimePickerProps & {
  tooltip?: string;
};

export const MegaDateTimePickerWithTooltip = ({
  tooltip,
  ...props
}: MegaDateTimePickerWithTooltip) => {
  const tooltipId = useTooltipId();
  return (
    <div data-tooltip-id={tooltip ? tooltipId : undefined}>
      <RenderMegaDateTimePicker {...props} />
      {tooltip && <FieldTooltip id={tooltipId} text={tooltip} />}
    </div>
  );
};
